import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';

import CreateProjectDropdown from '../../../components/DropdownStyle/CreateProjectDropdown';
import Select from '../../../components/ReactSelectDropdown';
import { filterOption } from '../../../components/ReactSelectDropdown/ReactSelectDropdown';
import SearchWithButton from '../../../components/Search/SearchWithButton';
import {
  FilterLabel,
  InputWithLabelSection,
} from '../../../styledComponents/common';
import { FilterContainer } from '../../../styledComponents/tasks';

const EmtithalMasterTasksFilter = ({
  publicZoneList,
  getOfficesShortList,
  handleSearchByName,
  handleClearSearch,
  onFilterChange,
  officesShortList,
}) => {
  const { t } = useTranslation();
  const [selectedPublicZone, setSelectedPublicZone] = useState({
    label: t('all_public_zones'),
    value: null,
  });
  const [selectedOffice, setSelectedOffice] = useState({
    label: t('all_offices'),
    value: null,
  });
  const [taskName, setTaskName] = useState('');
  const [taskCode, setTaskCode] = useState('');
  const [officeOptions, setOfficeOptions] = useState([
    {
      label: t('all_offices'),
      value: null,
    },
  ]);
  const [publicZoneOptions, setPublicZoneOptions] = useState([
    {
      label: t('all_public_zones'),
      value: null,
    },
  ]);

  useEffect(() => {
    if (publicZoneList && publicZoneList.length > 0) {
      const newArr = [
        {
          label: t('all_public_zones'),
          value: null,
        },
        ...publicZoneList.map(zone => ({
          label: zone.name,
          value: zone.id,
        })),
      ];
      setPublicZoneOptions(newArr);
    }
  }, [publicZoneList]);

  useEffect(() => {
    if (selectedPublicZone?.value) {
      getOfficesShortList({
        public_zone_id: selectedPublicZone.value,
      });
    } else {
      getOfficesShortList();
    }
    setSelectedOffice({
      label: t('all_offices'),
      value: null,
    });
  }, [selectedPublicZone, getOfficesShortList]);

  useEffect(() => {
    if (officesShortList) {
      const newArr = [
        {
          label: t('all_offices'),
          value: null,
        },
        ...(officesShortList || []).map(office => ({
          label: office.name,
          value: office.id,
        })),
      ];
      setOfficeOptions(newArr);

      if (selectedOffice && selectedOffice.value) {
        const officeExists = newArr.some(
          option => option.value === selectedOffice.value,
        );
        if (!officeExists) {
          setSelectedOffice({
            label: t('all_offices'),
            value: null,
          });
          updateFilters({
            ...getFilterState(),
            office: null,
          });
        }
      }
    }
  }, [officesShortList]);

  const getFilterState = () => ({
    publicZone: selectedPublicZone,
    office: selectedOffice,
    taskName,
    taskCode,
  });

  const updateFilters = filters => {
    if (onFilterChange) {
      onFilterChange(filters);
    }
  };

  const debouncedUpdateSearchFilters = useDebouncedCallback(filters => {
    updateFilters(filters);
  }, 400);

  const handlePublicZoneChange = zone => {
    setSelectedPublicZone(zone);
    updateFilters({
      ...getFilterState(),
      publicZone: zone,
      office: null,
    });
  };

  const handleOfficeChange = office => {
    setSelectedOffice(office);
    updateFilters({
      ...getFilterState(),
      office,
    });
  };

  const handleTaskNameChange = name => {
    setTaskName(name);
    debouncedUpdateSearchFilters({
      ...getFilterState(),
      taskName: name,
      taskCode,
    });
  };

  const handleTaskCodeChange = code => {
    setTaskCode(code);
    debouncedUpdateSearchFilters({
      ...getFilterState(),
      taskName,
      taskCode: code,
    });
  };

  const handleClearTaskName = () => {
    setTaskName('');
    debouncedUpdateSearchFilters.cancel();
    updateFilters({
      ...getFilterState(),
      taskName: '',
      taskCode,
    });
    if (handleClearSearch) handleClearSearch();
  };

  const handleClearTaskCode = () => {
    setTaskCode('');
    debouncedUpdateSearchFilters.cancel();
    updateFilters({
      ...getFilterState(),
      taskName,
      taskCode: '',
    });
    if (handleClearSearch) handleClearSearch();
  };

  return (
    <FilterContainer>
      <InputWithLabelSection width="200px">
        <FilterLabel>{t('public_zone')}</FilterLabel>
        <Select
          isSearchable
          value={selectedPublicZone}
          options={publicZoneOptions}
          placeholder={`${t('select_public_zone')}...`}
          onChange={handlePublicZoneChange}
          styles={CreateProjectDropdown()}
          filterOption={filterOption}
        />
      </InputWithLabelSection>
      <InputWithLabelSection width="200px">
        <FilterLabel>{t('office')}</FilterLabel>
        <Select
          isSearchable
          value={selectedOffice}
          options={officeOptions}
          placeholder={`${t('select_office')}...`}
          onChange={handleOfficeChange}
          styles={CreateProjectDropdown()}
          filterOption={filterOption}
        />
      </InputWithLabelSection>
      <SearchWithButton
        itemName={t('task_name')}
        label={t('search_by_task_name')}
        searchTermName={t('task_name')}
        searchInput={taskName}
        onInputChange={e => handleTaskNameChange(e.target.value)}
        handleSearch={() =>
          handleSearchByName && handleSearchByName(taskName)
        }
        handleClearSearch={handleClearTaskName}
      />
      <SearchWithButton
        itemName={t('task_code')}
        label={t('search_by_task_code')}
        searchTermName={t('task_code')}
        searchInput={taskCode}
        onInputChange={e => handleTaskCodeChange(e.target.value)}
        handleSearch={() =>
          handleSearchByName && handleSearchByName(taskCode)
        }
        handleClearSearch={handleClearTaskCode}
      />
    </FilterContainer>
  );
};

export default EmtithalMasterTasksFilter;
